import React, { useState } from 'react';
import { Send, Zap, Users, ChevronDown, Database } from 'lucide-react';
import { agentQueries, DASHBOARD_DATA } from '../../context/AgentDataContext';

export default function AgentManager() {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'agent-intro',
      content: 'Hello! I\'m the Agent Manager. I coordinate all 6 procurement agents to answer your questions. Ask me anything about demand, orders, claims, receiving, supplier performance, or order confirmations.',
      timestamp: '9:00 AM',
      chainOfThought: null
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expandedChain, setExpandedChain] = useState(null);

  const agents = [
    { id: 'demand', name: 'Demand Agent', emoji: '📊', role: 'Forecasts demand & analyzes trends' },
    { id: 'ordering', name: 'Ordering Agent', emoji: '📦', role: 'Generates & optimizes POs' },
    { id: 'claims', name: 'Claims Agent', emoji: '🎯', role: 'Files & tracks disputes' },
    { id: 'receiving', name: 'Receiving Agent', emoji: '📥', role: 'Matches goods vs orders' },
    { id: 'scoring', name: 'Scoring Agent', emoji: '⭐', role: 'Tracks supplier reliability' },
    { id: 'confirmation', name: 'Confirmation Agent', emoji: '✓', role: 'Manages supplier communication' }
  ];

  const generateAgentResponse = (agentId) => {
    try {
      const queries = agentQueries[agentId];
      let answer, confidence, data;

      switch (agentId) {
        case 'demand': {
          const trends = queries.analyzeTrends();
          const forecast = queries.getForecast();
          answer = `Based on ${forecast.posDataPoints} POS data points, ${trends.highestGrowth} demand is up ${Math.round(trends.growth)} units (${trends.confidence}% confidence). Weekend demand peak detected.`;
          confidence = trends.confidence;
          data = Object.entries(forecast.weeklyForecast).map(([cat, d]) =>
            `${cat.charAt(0).toUpperCase() + cat.slice(1)}: ${d.forecast} units (vs ${d.baseline} baseline)`
          );
          break;
        }

        case 'ordering': {
          const status = queries.getOrderStatus();
          const reliable = queries.getReliableSuppliers();
          answer = `${status.confirmedPos}/12 POs confirmed. ${status.pendingPos} awaiting response. Top 3 suppliers (${reliable.suppliers.map(s => s.name).join(', ')}) should handle 60% of orders.`;
          confidence = 87;
          data = [
            `Confirmed: ${status.confirmedPos} POs`,
            `Pending: ${status.pendingPos} POs`,
            `Avg lead time: ${status.averageLeadTime}h`,
            `Reliable suppliers: ${reliable.count}/5`
          ];
          break;
        }

        case 'claims': {
          const claims = queries.getClaimsSummary();
          const recovery = queries.getRecoveryStatus();
          answer = `${claims.thisWeekFiled} claims filed this week, $${claims.thisWeekRecovered.toLocaleString()} recovered. Top issue: ${recovery.topCategory} ($${recovery.amount}). ${recovery.pending} claims pending follow-up.`;
          confidence = 100;
          data = Object.entries(claims.byCategory).map(([key, val]) =>
            `${key.replace(/([A-Z])/g, ' $1').trim()}: ${val.count} claims ($${val.amount})`
          );
          break;
        }

        case 'receiving': {
          const receiving = queries.getTodayStatus();
          const disc = queries.getDiscrepancyRate();
          answer = `${receiving.todayProcessed} orders processed today with ${disc.matchRate}% accuracy. ${disc.discrepancies === 0 ? 'No discrepancies detected. Perfect alignment.' : `${disc.discrepancies} discrepancies found.`}`;
          confidence = 98;
          data = [
            `Orders processed: ${receiving.todayProcessed}`,
            `Match rate: ${receiving.matchRate}%`,
            `Avg processing time: ${receiving.avgProcessingTime}min`,
            `Discrepancies: ${disc.discrepancies}`
          ];
          break;
        }

        case 'scoring': {
          const suppliers = queries.getSupplierScores();
          const decliners = queries.getDecliners();
          const top = suppliers[0];
          answer = `Supplier scores updated. ${top.name} leading at ${top.score}%. ${decliners.decliningCount} suppliers declining: ${decliners.suppliers.map(s => s.name).join(', ')}. Recommend reducing orders to declining suppliers.`;
          confidence = 96;
          data = suppliers.slice(0, 3).map(s => `${s.name}: ${s.score}% (${s.onTime}% on-time, ${s.orders} orders)`);
          break;
        }

        case 'confirmation': {
          const confirmations = queries.getConfirmationRate();
          answer = `Confirmation status: ${confirmations.confirmed}/${confirmations.confirmed + confirmations.pending} POs confirmed (${confirmations.confirmationRate}% rate). ${confirmations.pending} suppliers need follow-up via WhatsApp.`;
          confidence = 85;
          data = [
            `Confirmed: ${confirmations.confirmed} POs`,
            `Pending: ${confirmations.pending} POs`,
            `Confirmation rate: ${confirmations.confirmationRate}%`,
            `Avg wait: ${confirmations.avgWaitTime}`
          ];
          break;
        }

        default:
          answer = 'Agent data query failed';
          confidence = 0;
          data = [];
      }

      return { answer, confidence, data };
    } catch (error) {
      console.error(`Error generating response for ${agentId}:`, error);
      return {
        answer: 'Unable to process query at this time',
        confidence: 0,
        data: []
      };
    }
  };

  const getDataSources = (agentId) => {
    const sources = {
      demand: ['POS Data (342 points)', 'Weekly Forecast', 'Category Trends'],
      ordering: ['Order Status', 'Supplier Scores', 'Lead Times'],
      claims: ['Claims Database', 'Recovery Tracking', 'Category Analysis'],
      receiving: ['Today\'s Receipts', 'PO Matching', 'Discrepancy Log'],
      scoring: ['Supplier Scores', 'Performance Trends', 'Reliability Metrics'],
      confirmation: ['PO Confirmations', 'Response Tracking', 'WhatsApp Status']
    };
    return sources[agentId] || [];
  };

  const generateChainOfThought = (userQuery) => {
    const lowerQuery = userQuery.toLowerCase();
    const chainSteps = [];

    if (lowerQuery.includes('demand') || lowerQuery.includes('forecast') || lowerQuery.includes('trend')) {
      chainSteps.push('demand');
    }
    if (lowerQuery.includes('order') || lowerQuery.includes('po') || lowerQuery.includes('supplier')) {
      chainSteps.push('ordering');
      chainSteps.push('scoring');
    }
    if (lowerQuery.includes('claim') || lowerQuery.includes('short') || lowerQuery.includes('issue')) {
      chainSteps.push('claims');
    }
    if (lowerQuery.includes('receiv') || lowerQuery.includes('match') || lowerQuery.includes('invoice')) {
      chainSteps.push('receiving');
    }
    if (lowerQuery.includes('confirm') || lowerQuery.includes('waiting')) {
      chainSteps.push('confirmation');
    }

    // Default: consult all agents for comprehensive answer
    if (chainSteps.length === 0) {
      return ['demand', 'ordering', 'claims', 'receiving', 'scoring', 'confirmation'];
    }

    return chainSteps;
  };

  const quickPrompts = [
    { label: 'Show ROI', query: 'What is the total margin recovered and labor freed this month?' },
    { label: 'Supplier Status', query: 'Which suppliers are declining in performance and need attention?' },
    { label: 'Forecast', query: 'What is the demand forecast for next week by category?' },
    { label: 'Claims Status', query: 'How many claims were filed this week and what is the recovery amount?' }
  ];

  const handleQuickPrompt = (query) => {
    setInput(query);
    setTimeout(() => handleSendMessage(query), 100);
  };

  const handleSendMessage = async (messageOverride) => {
    const messageText = messageOverride || input;
    if (!messageText.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: messageText,
      timestamp: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Simulate agent coordination
    setTimeout(() => {
      const consultedAgents = generateChainOfThought(messageText);

      const chainOfThought = {
        query: messageText,
        steps: consultedAgents.map(agentId => {
          const agent = agents.find(a => a.id === agentId);
          const response = generateAgentResponse(agentId);
          return {
            agentId,
            agentName: agent.name,
            emoji: agent.emoji,
            status: 'completed',
            answer: response.answer,
            confidence: response.confidence,
            data: response.data,
            processingTime: Math.floor(Math.random() * 300) + 150,
            dataSourcesQueried: getDataSources(agentId)
          };
        }),
        synthesisTime: Math.floor(Math.random() * 200) + 100,
        finalDecision: `Based on analysis of ${consultedAgents.length} agents querying real dashboard data:`
      };

      const managerMessage = {
        id: messages.length + 2,
        type: 'agent-manager',
        content: chainOfThought.finalDecision + '\n\n' +
          chainOfThought.steps.map(step => {
            return `${step.emoji} ${step.answer}`;
          }).join('\n\n'),
        timestamp: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
        chainOfThought
      };

      setMessages(prev => [...prev, managerMessage]);
      setIsLoading(false);
    }, 1500);
  };

  return (
    <div className="flex flex-col h-full bg-[#050506]">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.map(msg => (
          <div key={msg.id}>
            {msg.type === 'user' && (
              <div className="flex justify-end mb-4">
                <div className="max-w-md bg-[#10b981] text-black rounded-lg p-4">
                  <p className="text-sm">{msg.content}</p>
                  <p className="text-xs text-black/60 mt-1">{msg.timestamp}</p>
                </div>
              </div>
            )}

            {msg.type === 'agent-intro' && (
              <div className="max-w-2xl bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">🤖</div>
                  <div className="flex-1">
                    <p className="font-medium text-white mb-1">Agent Manager</p>
                    <p className="text-white/70 text-sm">{msg.content}</p>
                    <p className="text-xs text-white/50 mt-2">{msg.timestamp}</p>
                  </div>
                </div>
              </div>
            )}

            {msg.type === 'agent-manager' && (
              <div className="max-w-2xl">
                {/* Manager Response */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-3">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🤖</div>
                    <div className="flex-1">
                      <p className="font-medium text-white mb-2">Agent Manager Response</p>
                      <p className="text-white/80 text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className="text-xs text-white/50 mt-2">{msg.timestamp}</p>
                    </div>
                  </div>
                </div>

                {/* Chain of Thought */}
                {msg.chainOfThought && (
                  <div className="bg-white/[0.03] border border-white/5 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedChain(expandedChain === msg.id ? null : msg.id)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Zap size={16} className="text-yellow-400" />
                        <span className="text-sm font-medium text-white/80">
                          Agent Coordination Chain ({msg.chainOfThought.steps.length} agents)
                        </span>
                      </div>
                      <ChevronDown
                        size={16}
                        className={`text-white/50 transition-transform ${
                          expandedChain === msg.id ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {expandedChain === msg.id && (
                      <div className="border-t border-white/5 p-4 space-y-3">
                        {msg.chainOfThought.steps.map((step, idx) => (
                          <div key={idx} className="bg-white/5 rounded p-3 space-y-2">
                            <div className="flex items-start gap-3">
                              <div className="text-lg flex-shrink-0">{step.emoji}</div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <p className="font-medium text-white text-sm">{step.agentName}</p>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">
                                      {step.confidence}% confidence
                                    </span>
                                    <span className="text-xs text-white/50">{step.processingTime}ms</span>
                                  </div>
                                </div>
                                <p className="text-white/70 text-xs mb-2">{step.answer}</p>
                              </div>
                            </div>

                            {/* Data sources */}
                            {step.dataSourcesQueried && step.dataSourcesQueried.length > 0 && (
                              <div className="flex flex-wrap gap-1 pl-7">
                                <Database size={12} className="text-white/40 flex-shrink-0 mt-0.5" />
                                {step.dataSourcesQueried.map((source, i) => (
                                  <span key={i} className="text-[10px] bg-white/10 text-white/60 px-1.5 py-0.5 rounded">
                                    {source}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Supporting data points */}
                            {step.data.length > 0 && (
                              <div className="pl-7 space-y-1">
                                {step.data.map((datum, i) => (
                                  <p key={i} className="text-white/50 text-[11px]">
                                    → {datum}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                        <div className="pt-2 border-t border-white/5 text-xs text-white/50">
                          Total synthesis time: {msg.chainOfThought.synthesisTime}ms
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="max-w-2xl bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#10b981] rounded-full animate-pulse" />
              <p className="text-white/60 text-sm">Coordinating agents...</p>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-white/10 p-6 space-y-4">
        {/* Quick Prompts - Only show on first message */}
        {messages.length === 1 && (
          <div className="space-y-2">
            <p className="text-xs text-white/50 font-medium">Quick Questions:</p>
            <div className="grid grid-cols-2 gap-2">
              {quickPrompts.map((prompt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickPrompt(prompt.query)}
                  disabled={isLoading}
                  className="text-left px-3 py-2 bg-white/5 hover:bg-white/10 disabled:opacity-50 border border-white/10 rounded text-xs text-white/80 transition-colors"
                >
                  {prompt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleSendMessage()}
            placeholder="Ask about demand, orders, claims, suppliers..."
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#10b981]/50"
          />
          <button
            onClick={() => handleSendMessage()}
            disabled={!input.trim() || isLoading}
            className="bg-[#10b981] hover:bg-[#0da76f] disabled:opacity-50 text-black px-4 py-3 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Send size={16} />
          </button>
        </div>

        {/* Agent Pool Status */}
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <Users size={14} className="text-white/50" />
            <p className="text-xs text-white/50">Available Agents</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {agents.map(agent => (
              <div
                key={agent.id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white/5 border border-white/10 rounded text-xs hover:bg-white/10 transition-colors cursor-help"
                title={agent.role}
              >
                <span>{agent.emoji}</span>
                <span className="text-white/70">{agent.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
